import { useEffect } from 'react';
import { useRef } from 'react';
import { ReadWritePatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadWritePattern';
import { DataSetWithExternalMergeData } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { CreateOrUpdateDataSetRequestBodyType } from 'libs/end-to-end/src/types/databases/data-sets/create-or-update-data-set-request-body';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { DataSetType } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { IntegrationType } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { SupplementalDatasetType } from 'libs/end-to-end/src/datasets';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { DataSetEditHTTPIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditHTTPIntegration';
import { DataSetEditMergeIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditMergeIntegration';
import { DataSetEditPostgresIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditPostgresIntegration';
import { DataSetEditCompositeHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditComposite';
import { DataSetEditSupplementalHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSupplemental';
import { DbtAction } from 'libs/end-to-end/src/types/databases/integrations/snowflake-integration';
import { DataSetEditSqlServerIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSqlServerIntegration';

export const useDataSetEditPageAutoloadEffect = ({
  pattern,
  setDisplayName,
  setIsArchived,
  setDataSetType,
  setFileName,
  setIntegrationType,
  setSupplementalDataSetType,
  setFileNameSecretReference,
  setClientIdSecretReference,
  setUsernameSecretReference,
  setFolderIdSecretReference,
  setClientSecretSecretReference,
  setOauthUrlSecretReference,
  setApiBaseUrlSecretReference,
  setApiKeySecretReference,
  setPasswordSecretReference,
  setEmployeeHistoryUrlSecretReference,
  setStaffingEventsUrlSecretReference,
  setCompensationChangeHistoryUrlSecretReference,
  setCandidatesUrlSecretReference,
  httpIntegrationHook,
  postgresIntegrationHook,
  mergeIntegrationHook,
  compositeHook,
  supplementalHook,
  setExternalMergeLink,
  setSslCertSecretReference,
  setKeyCertSecretReference,
  setDbtAction,
  setAgentMode,
  setAgentDateCap,
  sqlServerIntegrationHook,
}: {
  pattern: ReadWritePatternHook<
    DataSetWithExternalMergeData,
    CreateOrUpdateDataSetRequestBodyType
  >;
  setDisplayName: Setter<string>;
  setIsArchived: Setter<boolean>;
  setDataSetType: Setter<DataSetType>;
  setFileName: Setter<string>;
  setIntegrationType: Setter<IntegrationType>;
  setSupplementalDataSetType: Setter<SupplementalDatasetType>;
  setClientIdSecretReference: Setter<SecretReference | null>;
  setClientSecretSecretReference: Setter<SecretReference | null>;
  setUsernameSecretReference: Setter<SecretReference | null>;
  setFolderIdSecretReference: Setter<SecretReference | null>;
  setFileNameSecretReference: Setter<SecretReference | null>;
  setOauthUrlSecretReference: Setter<SecretReference | null>;
  setApiBaseUrlSecretReference: Setter<SecretReference | null>;
  setApiKeySecretReference: Setter<SecretReference | null>;
  setPasswordSecretReference: Setter<SecretReference | null>;
  setEmployeeHistoryUrlSecretReference: Setter<SecretReference | null>;
  setStaffingEventsUrlSecretReference: Setter<SecretReference | null>;
  setCompensationChangeHistoryUrlSecretReference: Setter<SecretReference | null>;
  setCandidatesUrlSecretReference: Setter<SecretReference | null>;
  httpIntegrationHook: DataSetEditHTTPIntegrationHook;
  postgresIntegrationHook: DataSetEditPostgresIntegrationHook;
  mergeIntegrationHook: DataSetEditMergeIntegrationHook;
  compositeHook: DataSetEditCompositeHook;
  supplementalHook: DataSetEditSupplementalHook;
  setExternalMergeLink: Setter<string | null>;
  setSslCertSecretReference: Setter<SecretReference | null>;
  setKeyCertSecretReference: Setter<SecretReference | null>;
  setDbtAction: Setter<DbtAction>;
  setAgentMode: Setter<boolean>;
  setAgentDateCap: Setter<string>;
  sqlServerIntegrationHook: DataSetEditSqlServerIntegrationHook;
}) => {
  const autoloadedRef = useRef(false);

  // eslint-disable-next-line complexity
  return useEffect(() => {
    if (autoloadedRef.current) {
      return;
    }
    if (pattern.status === 'done' && !pattern.saved) {
      autoloadedRef.current = true;

      const entity = pattern.entity;
      const config = entity.config;

      setDisplayName(entity.displayName);
      setIsArchived(entity.isArchived);
      setDataSetType(config.dataSetType);
      setFileName(config.dataSetType === 'File' ? config.fileName : '');
      setIntegrationType(
        config.dataSetType === 'Integration'
          ? config.integration.integrationType
          : 'ShareFile',
      );
      setSupplementalDataSetType(
        config.dataSetType === 'Supplemental'
          ? config.supplementalDataSetType
          : 'external_pay_benchmarking',
      );

      setClientIdSecretReference(null);
      setClientSecretSecretReference(null);
      setOauthUrlSecretReference(null);
      setApiBaseUrlSecretReference(null);
      setFolderIdSecretReference(null);
      setFileNameSecretReference(null);
      setApiKeySecretReference(null);
      setUsernameSecretReference(null);
      setPasswordSecretReference(null);
      setEmployeeHistoryUrlSecretReference(null);
      setStaffingEventsUrlSecretReference(null);
      setCompensationChangeHistoryUrlSecretReference(null);
      setCandidatesUrlSecretReference(null);

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'ShareFile'
      ) {
        setClientIdSecretReference(
          config.integration.clientIdSecretReference ?? null,
        );
        setClientSecretSecretReference(
          config.integration.clientSecretSecretReference ?? null,
        );
        setOauthUrlSecretReference(
          config.integration.oauthUrlSecretReference ?? null,
        );
        setApiBaseUrlSecretReference(
          config.integration.apiBaseUrlSecretReference ?? null,
        );
        setUsernameSecretReference(
          config.integration.usernameSecretReference ?? null,
        );
        setPasswordSecretReference(
          config.integration.passwordSecretReference ?? null,
        );
        setFolderIdSecretReference(
          config.integration.folderIdSecretReference ?? null,
        );
        setFileNameSecretReference(
          config.integration.fileNameSecretReference ?? null,
        );
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'ADP'
      ) {
        setClientIdSecretReference(
          config.integration.clientIdSecretReference ?? null,
        );
        setClientSecretSecretReference(
          config.integration.clientSecretSecretReference ?? null,
        );
        setSslCertSecretReference(
          config.integration.sslCertSecretReference ?? null,
        );
        setKeyCertSecretReference(
          config.integration.keyCertSecretReference ?? null,
        );
        setAgentMode(config.integration.agentMode ?? false);
        setAgentDateCap(config.integration.agentDateCap ?? 'all');
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'Greenhouse'
      ) {
        setApiKeySecretReference(
          config.integration.apiKeySecretReference ?? null,
        );
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'Workday'
      ) {
        setUsernameSecretReference(
          config.integration.usernameSecretReference ?? null,
        );
        setPasswordSecretReference(
          config.integration.passwordSecretReference ?? null,
        );
        setEmployeeHistoryUrlSecretReference(
          config.integration.employeeHistoryUrlSecretReference ?? null,
        );
        setStaffingEventsUrlSecretReference(
          config.integration.staffingEventsUrlSecretReference ?? null,
        );
        setCompensationChangeHistoryUrlSecretReference(
          config.integration.compensationChangeHistoryUrlSecretReference ??
            null,
        );
        setCandidatesUrlSecretReference(
          config.integration.candidatesUrlSecretReference ?? null,
        );
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'HTTP'
      ) {
        httpIntegrationHook.setHttpIntegration(config.integration);
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'Postgres'
      ) {
        postgresIntegrationHook.setPostgresIntegration(config.integration);
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'Merge'
      ) {
        mergeIntegrationHook.setMergeIntegration(config.integration);
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'Snowflake'
      ) {
        setDbtAction(config.integration.dbtAction);
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'Peregrine'
      ) {
        setUsernameSecretReference(
          config.integration.usernameSecretReference ?? null,
        );
        setPasswordSecretReference(
          config.integration.passwordSecretReference ?? null,
        );
      }

      if (
        config.dataSetType === 'Integration' &&
        config.integration.integrationType === 'SqlServer'
      ) {
        sqlServerIntegrationHook.setSqlServerIntegration(config.integration);
      }

      if (config.dataSetType === 'Supplemental') {
        supplementalHook.setConfig(config);
      }

      if (config.dataSetType === 'Composite') {
        compositeHook.setConfig(config);
      }

      setExternalMergeLink(entity.externalMergeLink ?? null);

      return;
    }
  }, [
    pattern,
    httpIntegrationHook,
    setDisplayName,
    setDataSetType,
    setFileName,
    setIntegrationType,
    setSupplementalDataSetType,
    setClientIdSecretReference,
    setClientSecretSecretReference,
    setOauthUrlSecretReference,
    setApiBaseUrlSecretReference,
    setFolderIdSecretReference,
    setFileNameSecretReference,
    setApiKeySecretReference,
    setUsernameSecretReference,
    setPasswordSecretReference,
    setEmployeeHistoryUrlSecretReference,
    setStaffingEventsUrlSecretReference,
    setCompensationChangeHistoryUrlSecretReference,
    setCandidatesUrlSecretReference,
    mergeIntegrationHook,
    compositeHook,
    supplementalHook,
    setIsArchived,
    postgresIntegrationHook,
    setExternalMergeLink,
    setSslCertSecretReference,
    setKeyCertSecretReference,
    setDbtAction,
    setAgentMode,
    setAgentDateCap,
    sqlServerIntegrationHook,
  ]);
};
